import {
  AnalyticsStore,
  codeSplit,
  Controller,
  FetchOptions,
  PageStore,
  FetchPageOptions
} from "bernie-core";
import { PageData } from "bernie-http";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import {
  Composition,
  ExtendedContextStore,
  ViewModelResponse
} from "typings/flexFramework/FlexDefinitions";
import { CookiesStore } from "stores/CookiesStore";
import { StaticMapStore } from "src/stores/staticMap/StaticMapStore";
import { CompositionStore } from "stores/CompositionStore";
import { ServerWizardStateStore } from "stores/wizard/ServerWizardStateStore";
import { BlossomCodeSplitterFactory } from "components/flexFramework/CodeSplitter/BlossomCodeSplitterFactory";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    compositionStore: CompositionStore;
  };
}

export class LandingController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  public path = "/*";
  public routeName = "flex-LandingPage";
  public bundles = ["flex-LandingPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-LandingPage" */ "views/LandingPage") as any;
    let composition: Composition | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = ((window as unknown) as WindowWithState).__STATE__;
      composition =
        serializedState && serializedState.compositionStore
          ? serializedState.compositionStore.composition
          : undefined;
    }

    await BlossomCodeSplitterFactory.get({ composition }).codeSplitAllReactComponents();

    return routeComponent;
  });

  public fetch(options: FetchOptions): Promise<any> {
    const { stores, request } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const cookiesStore = stores && stores.get<CookiesStore>("cookies");
    const mapStore = stores && stores.get<StaticMapStore>("staticMap");
    const compositionStore = stores && stores.get<CompositionStore>("compositionStore");
    const wizardState = stores && stores.get<ServerWizardStateStore>("wizardState");
    //  Get cookie string for wizard
    cookiesStore.cookie = (request && request.headers && request.headers.cookie) || "";
    if (wizardState && wizardState.setLastSearchState) {
      wizardState.setLastSearchState(cookiesStore.getCookie("lastSearchCookie")!, contextStore);
    }

    // We get it from the middleware which hijacks the contextStore
    const viewModelResponse = contextStore.viewModelResponse as ViewModelResponse;

    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(
      viewModelResponse,
      pageStore,
      contextStore,
      analyticsStore,
      footer
    );

    compositionStore.setupCompositionStore(viewModelResponse, contextStore, mapStore);

    return Promise.resolve({});
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const viewModelResponse = options.context.viewModelResponse as ViewModelResponse;
    const additionalScripts = BlossomCodeSplitterFactory.get({
      composition: viewModelResponse.composition
    })
      .getWebpackReactComponents()
      .map(component => component && component.webpackChunkName);

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(
      ({ default: _ }) => {
        return (
          _.buildPageDataWithSeoMetaDataFromViewModel(viewModelResponse) || {
            title: viewModelResponse.composition.title
          }
        );
      }
    );

    // Basically giving a list of webpackChunkNames that we want
    return Promise.resolve(seoMetaData).then(m => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey)
      };
    });
  }
}
